import React, { useState, useEffect } from 'react';
import { Copy, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { AccrezeoSheet } from '@/components/shared/AccrezeoSheet';
import {
    DS_COLORS,
    DS_SPACING,
    DS_TYPOGRAPHY,
} from '@/config/ui-invariants';

/**
 * FounderDetailPanel — founder contact sheet with LinkedIn URL + outreach
 * message. Shared between the Deal Sourcing agent page and the Work Inbox
 * DealSourcingTaskDetail renderer to satisfy PKG-009 (surface parity).
 *
 * Migrated to AccrezeoSheet in WI-DESIGN-SHEET-002: Radix Dialog now owns
 * focus trap, Escape handling, overlay click-to-close, and slide animation.
 * Hand-rolled position:fixed / focus management / click-outside scaffolding
 * removed.
 *
 * All user-facing copy comes from the `labels` prop (config-driven — caller
 * passes AGENT_CONFIG.<agent>.labels). No hardcoded strings.
 *
 * @param {object} props
 * @param {object} props.founder - Founder object with full_name, designation, linkedin_url.
 * @param {string|null} props.outreachMessage - MUST be pre-resolved by the caller.
 *   Index-keyed resolution lives in DealCard.jsx:_resolveOutreachMessage.
 *   If you mount this component outside DealCard (e.g. DealSourcingTaskDetail),
 *   you must resolve the message before passing it in. Passing an un-resolved
 *   index or a raw outreach_messages_by_founder entry will render garbled text
 *   for the CEO. See M-REG-003.
 * @param {object} props.labels - Config-driven label strings:
 *   { founderPanelLinkedinSection, founderPanelMessageSection,
 *     founderPanelNoMessage, founderPanelCopy, founderPanelCopied,
 *     founderPanelClose }
 * @param {Function} props.onClose - Close handler.
 */
export function FounderDetailPanel({ founder, outreachMessage, labels, onClose }) {
    const [isOpen, setIsOpen] = useState(true);
    const [linkedinCopied, setLinkedinCopied] = useState(false);
    const [messageCopied, setMessageCopied] = useState(false);

    // Trigger Radix close animation, then unmount via parent after the transition.
    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    useEffect(() => { setIsOpen(true); }, []);

    const copyToClipboard = async (text, setCopied) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const hasMessage = outreachMessage && outreachMessage.trim().length > 0;

    return (
        <AccrezeoSheet
            open={isOpen}
            onClose={handleClose}
            title={founder.full_name}
            subtitle={founder.designation || undefined}
            width="400px"
            ariaLabel={founder.full_name || 'Founder Details'}
        >
            {founder.linkedin_url && (
                <div style={{ marginBottom: `${DS_SPACING.cardPadding}px` }}>
                    <div
                        style={{
                            ...DS_TYPOGRAPHY.sectionLabel,
                            color: DS_COLORS.text.secondary,
                            marginBottom: `${DS_SPACING.labelToContent}px`,
                        }}
                    >
                        {labels.founderPanelLinkedinSection}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: `${DS_SPACING.buttonGap}px`,
                        }}
                    >
                        <span
                            style={{
                                ...DS_TYPOGRAPHY.bodySm,
                                color: DS_COLORS.text.primary,
                                wordBreak: 'break-all',
                                flex: 1,
                            }}
                        >
                            {founder.linkedin_url}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(founder.linkedin_url, setLinkedinCopied)}
                            className="gap-1 shrink-0"
                        >
                            {linkedinCopied ? (
                                <>
                                    <Check weight="bold" className="w-3.5 h-3.5" /> {labels.founderPanelCopied}
                                </>
                            ) : (
                                <>
                                    <Copy weight="bold" className="w-3.5 h-3.5" /> {labels.founderPanelCopy}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            <div>
                <div
                    style={{
                        ...DS_TYPOGRAPHY.sectionLabel,
                        color: DS_COLORS.text.secondary,
                        marginBottom: `${DS_SPACING.labelToContent}px`,
                    }}
                >
                    {labels.founderPanelMessageSection}
                </div>
                {hasMessage ? (
                    <div>
                        <div
                            style={{
                                ...DS_TYPOGRAPHY.bodySm,
                                color: DS_COLORS.text.primary,
                                whiteSpace: 'pre-wrap',
                                marginBottom: `${DS_SPACING.labelToContent}px`,
                            }}
                        >
                            {outreachMessage}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(outreachMessage, setMessageCopied)}
                            className="gap-1"
                        >
                            {messageCopied ? (
                                <>
                                    <Check weight="bold" className="w-3.5 h-3.5" /> {labels.founderPanelCopied}
                                </>
                            ) : (
                                <>
                                    <Copy weight="bold" className="w-3.5 h-3.5" /> {labels.founderPanelCopy}
                                </>
                            )}
                        </Button>
                    </div>
                ) : (
                    <span
                        style={{
                            ...DS_TYPOGRAPHY.bodySm,
                            color: DS_COLORS.text.muted,
                        }}
                    >
                        {labels.founderPanelNoMessage}
                    </span>
                )}
            </div>
        </AccrezeoSheet>
    );
}

export default FounderDetailPanel;
